import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { BtnPrimary } from '@components/buttons';
import { AsyncForm, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { showToast } from '@components/toaster';
import { UserProfileIcon } from '@components/avatars';
import { HeadingSecondary } from '@course/components/headings';
import { RsvpSection } from './rsvp-section';

type Props = {
  courseId: UUID;
  meetingId: UUID;
};

type HostCandidate = {
  id: UUID;
  email: string;
  name?: string;
  displayName?: string;
  profilePhotoUrl?: string;
  level: 'guide' | 'facilitator';
};

export function InviteTab({ courseId, meetingId }: Props) {
  const [data, setData] = useAsyncState(
    () => rpx.meetings.getMeetingInviteOverrides({ courseId, meetingId }),
    [courseId, meetingId],
  );

  const [memberDescOverride, setMemberDescOverride] = useState<string | undefined>(undefined);
  const [hostDescOverride, setHostDescOverride] = useState<string | undefined>(undefined);
  const [bufferOverride, setBufferOverride] = useState<string | undefined>(undefined);
  const [reminderOverride, setReminderOverride] = useState<string | undefined>(undefined);
  const [hostIdsOverride, setHostIdsOverride] = useState<UUID[] | undefined>(undefined);
  const [memberDescUse, setMemberDescUse] = useState<'default' | 'custom' | undefined>(undefined);
  const [hostDescUse, setHostDescUse] = useState<'default' | 'custom' | undefined>(undefined);
  const [bufferUse, setBufferUse] = useState<'default' | 'custom' | undefined>(undefined);
  const [reminderUse, setReminderUse] = useState<'default' | 'custom' | undefined>(undefined);
  const [hostsUse, setHostsUse] = useState<'default' | 'custom' | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  if (!data) {
    return (
      <div class="flex justify-center py-12">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  if (!data.calendarInvitesEnabled) {
    return (
      <div class="my-6 flex flex-col gap-2">
        <HeadingSecondary>Calendar invites are off for this course</HeadingSecondary>
        <p class="opacity-75">
          Per-meeting overrides only apply when auto calendar invites are enabled for the course.
          Enable it under <strong>Course Settings → Calendar Invites</strong>, then come back to
          customize this meeting.
        </p>
      </div>
    );
  }

  const candidates = data.hostCandidates as HostCandidate[];
  const defaults = data.courseDefaults;
  const overrides = data.meetingOverrides;

  const currentMemberDescMode =
    memberDescUse ?? (overrides.memberInviteDescription == null ? 'default' : 'custom');
  const currentHostDescMode =
    hostDescUse ?? (overrides.hostInviteDescription == null ? 'default' : 'custom');
  const currentBufferMode =
    bufferUse ?? (overrides.hostBufferMinutes == null ? 'default' : 'custom');
  const currentReminderMode =
    reminderUse ?? (overrides.reminderMinutesBefore == null ? 'default' : 'custom');
  const currentHostsMode =
    hostsUse ??
    (overrides.hostUserIds == null || overrides.hostUserIds.length === 0 ? 'default' : 'custom');

  const currentMemberDesc =
    memberDescOverride ?? overrides.memberInviteDescription ?? defaults.memberInviteDescription;
  const currentHostDesc =
    hostDescOverride ?? overrides.hostInviteDescription ?? defaults.hostInviteDescription;
  const currentBuffer =
    bufferOverride ?? String(overrides.hostBufferMinutes ?? defaults.hostBufferMinutes);
  const currentReminder =
    reminderOverride ?? String(overrides.reminderMinutesBefore ?? defaults.reminderMinutesBefore);
  const currentHostIds = hostIdsOverride ?? overrides.hostUserIds ?? [];

  function toggleHost(userId: UUID) {
    const next = currentHostIds.includes(userId)
      ? currentHostIds.filter((id) => id !== userId)
      : [...currentHostIds, userId];
    setHostIdsOverride(next);
  }

  async function submit() {
    let buffer: number | null = null;
    let reminder: number | null = null;
    if (currentBufferMode === 'custom') {
      buffer = parseInt(currentBuffer, 10);
      if (Number.isNaN(buffer) || buffer < 0 || buffer > 120) {
        setValidationError('Host buffer must be a whole number between 0 and 120 minutes.');
        return;
      }
    }
    if (currentReminderMode === 'custom') {
      reminder = parseInt(currentReminder, 10);
      if (Number.isNaN(reminder) || reminder < 0 || reminder > 1440) {
        setValidationError('Reminder window must be a whole number between 0 and 1440 minutes.');
        return;
      }
    }
    if (currentHostsMode === 'custom' && currentHostIds.length === 0) {
      setValidationError(
        'Pick at least one host, or switch back to the course default to inherit the course host list.',
      );
      return;
    }
    if (currentMemberDescMode === 'custom' && currentMemberDesc.trim() === '') {
      setValidationError(
        'Member invite description is empty. Add some text, or switch back to the course default.',
      );
      return;
    }
    if (currentHostDescMode === 'custom' && currentHostDesc.trim() === '') {
      setValidationError(
        'Host invite description is empty. Add some text, or switch back to the course default.',
      );
      return;
    }
    setValidationError(undefined);
    setSaving(true);

    try {
      await rpx.meetings.setMeetingInviteOverrides({
        courseId,
        meetingId,
        memberInviteDescription: currentMemberDescMode === 'custom' ? currentMemberDesc : null,
        hostInviteDescription: currentHostDescMode === 'custom' ? currentHostDesc : null,
        hostBufferMinutes: currentBufferMode === 'custom' ? buffer : null,
        reminderMinutesBefore: currentReminderMode === 'custom' ? reminder : null,
        hostUserIds: currentHostsMode === 'custom' ? currentHostIds : null,
      });
    } catch (err) {
      const message = (err as { message?: unknown })?.message;
      if (typeof message === 'string' && message) {
        setValidationError(message);
        setSaving(false);
        return;
      }
      setSaving(false);
      throw err;
    }

    const fresh = await rpx.meetings.getMeetingInviteOverrides({ courseId, meetingId });
    setData(fresh);
    setMemberDescUse(undefined);
    setHostDescUse(undefined);
    setBufferUse(undefined);
    setReminderUse(undefined);
    setHostsUse(undefined);
    setMemberDescOverride(undefined);
    setHostDescOverride(undefined);
    setBufferOverride(undefined);
    setReminderOverride(undefined);
    setHostIdsOverride(undefined);
    setSaving(false);

    showToast({
      title: 'Overrides saved',
      message:
        'New calendar invites for this meeting will use these settings. Resend invites from the meeting page to push the change to attendees.',
      type: 'ok',
    });
  }

  return (
    <AsyncForm class="flex flex-col gap-6 my-6" onSubmit={submit}>
      <RsvpSection courseId={courseId} meetingId={meetingId} />

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <HeadingSecondary>Per-meeting calendar invite overrides</HeadingSecondary>
          <Subtext>
            By default this meeting inherits the course-level invite settings. Override any field
            below for one-off bonus sessions or speakers.
          </Subtext>
        </div>
      </div>

      <OverrideTextField
        label="Member invite description"
        helpText="Body text shown to members in the calendar event. Plain text — line breaks are preserved."
        mode={currentMemberDescMode}
        onModeChange={setMemberDescUse}
        defaultPreview={defaults.memberInviteDescription || '(no course default set)'}
        value={currentMemberDesc}
        onValueChange={setMemberDescOverride}
        rows={6}
      />

      <OverrideTextField
        label="Host invite description"
        helpText="Body text for the host event — typically sign-in URL, alternate host code, pre-call notes."
        mode={currentHostDescMode}
        onModeChange={setHostDescUse}
        defaultPreview={defaults.hostInviteDescription || '(no course default set)'}
        value={currentHostDesc}
        onValueChange={setHostDescOverride}
        rows={6}
      />

      <OverrideNumberField
        label="Host buffer (minutes)"
        helpText="Start the host's calendar event this many minutes before the meeting time."
        mode={currentBufferMode}
        onModeChange={setBufferUse}
        defaultValue={defaults.hostBufferMinutes}
        value={currentBuffer}
        onValueChange={setBufferOverride}
        min={0}
        max={120}
      />

      <OverrideNumberField
        label="Reminder window (minutes before)"
        helpText="How long before the meeting members' calendars should remind them."
        mode={currentReminderMode}
        onModeChange={setReminderUse}
        defaultValue={defaults.reminderMinutesBefore}
        value={currentReminder}
        onValueChange={setReminderOverride}
        min={0}
        max={1440}
      />

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4">
        <FormSubSection>
          <InputLabel>Hosts for this meeting</InputLabel>
          <Subtext>
            Course default:{' '}
            {defaults.hostUserIds && defaults.hostUserIds.length > 0
              ? `${defaults.hostUserIds.length} specific host${
                  defaults.hostUserIds.length === 1 ? '' : 's'
                } chosen on the course`
              : 'All guides + facilitators'}
            .
          </Subtext>
          <ModeRadios
            mode={currentHostsMode}
            onModeChange={setHostsUse}
            customLabel="Choose specific hosts for this meeting"
          />
          {currentHostsMode === 'custom' && (
            <div class="flex flex-col gap-2 pt-2">
              {candidates.length === 0 && (
                <span class="text-sm opacity-75">No guides or facilitators on this course.</span>
              )}
              {candidates.map((u) => {
                const isSelected = currentHostIds.includes(u.id);
                return (
                  <label
                    key={u.id}
                    class={`flex gap-3 cursor-pointer items-center border rounded-lg p-2 px-3 ${
                      isSelected ? 'border-indigo-500 ring-1 ring-indigo-500' : ''
                    }`}
                  >
                    <input type="checkbox" checked={isSelected} onChange={() => toggleHost(u.id)} />
                    <UserProfileIcon
                      user={{ name: u.name || u.email, profilePhotoUrl: u.profilePhotoUrl }}
                      size="w-7 h-7"
                    />
                    <span class="flex flex-col leading-snug text-sm">
                      <strong>{u.displayName || u.name || u.email}</strong>
                      <span class="opacity-75">
                        {u.email} · {u.level}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </FormSubSection>
      </div>

      {validationError && (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {validationError}
        </div>
      )}

      <footer>
        <BtnPrimary disabled={saving} class="rounded-full px-12">
          {saving ? 'Saving…' : 'Save overrides'}
        </BtnPrimary>
      </footer>
    </AsyncForm>
  );
}

function ModeRadios({
  mode,
  onModeChange,
  customLabel,
}: {
  mode: 'default' | 'custom';
  onModeChange: (mode: 'default' | 'custom') => void;
  customLabel: string;
}) {
  return (
    <div class="flex flex-col gap-2 pt-1">
      <label class="flex items-center gap-2 cursor-pointer">
        <input type="radio" checked={mode === 'default'} onChange={() => onModeChange('default')} />
        <span class="text-sm">Use course default</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <input type="radio" checked={mode === 'custom'} onChange={() => onModeChange('custom')} />
        <span class="text-sm">{customLabel}</span>
      </label>
    </div>
  );
}

function OverrideTextField({
  label,
  helpText,
  mode,
  onModeChange,
  defaultPreview,
  value,
  onValueChange,
  rows,
}: {
  label: string;
  helpText: string;
  mode: 'default' | 'custom';
  onModeChange: (mode: 'default' | 'custom') => void;
  defaultPreview: string;
  value: string;
  onValueChange: (v: string) => void;
  rows: number;
}) {
  return (
    <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4">
      <FormSubSection>
        <InputLabel>{label}</InputLabel>
        <Subtext>{helpText}</Subtext>
        <ModeRadios
          mode={mode}
          onModeChange={onModeChange}
          customLabel={`Override ${label.toLowerCase()} for this meeting`}
        />
        {mode === 'default' && (
          <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm whitespace-pre-wrap text-gray-700">
            {defaultPreview}
          </div>
        )}
        {mode === 'custom' && (
          <textarea
            class="ruz-input"
            rows={rows}
            value={value}
            onInput={(e) => onValueChange((e.target as HTMLTextAreaElement).value)}
          />
        )}
      </FormSubSection>
    </div>
  );
}

function OverrideNumberField({
  label,
  helpText,
  mode,
  onModeChange,
  defaultValue,
  value,
  onValueChange,
  min,
  max,
}: {
  label: string;
  helpText: string;
  mode: 'default' | 'custom';
  onModeChange: (mode: 'default' | 'custom') => void;
  defaultValue: number;
  value: string;
  onValueChange: (v: string) => void;
  min: number;
  max: number;
}) {
  return (
    <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4">
      <FormSubSection>
        <InputLabel>{label}</InputLabel>
        <Subtext>
          {helpText} Course default: <strong>{defaultValue}</strong>.
        </Subtext>
        <ModeRadios
          mode={mode}
          onModeChange={onModeChange}
          customLabel={`Override ${label.toLowerCase()} for this meeting`}
        />
        {mode === 'custom' && (
          <input
            class="ruz-input max-w-32"
            type="number"
            min={min}
            max={max}
            value={value}
            onInput={(e) => onValueChange((e.target as HTMLInputElement).value)}
          />
        )}
      </FormSubSection>
    </div>
  );
}
